import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProject } from "@lumanity/ui/projects";
import { ErrorBanner, toast } from "@lumanity/ui/primitives";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { isConflict } from "@/services/api";
import { outputKeys, useUpdateOutput } from "@/features/outputs/outputsApi";
import type { Output } from "@/features/outputs/types";
import { useCanWrite } from "./useCanWrite";
import DraftCanvas from "./DraftCanvas";
import { useOutlineMutations, useSections } from "./draftApi";
import OutlinePanel from "./OutlinePanel";
import SectionPanel from "./SectionPanel";
import { useProjectEvidence } from "./useProjectEvidence";

interface FirstDraftProps {
  output: Output;
  /** A section's text is being edited and not saved yet (the page guards its own stepper with this). */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * The First draft screen (FRD Stage 5): the outline on the left, the draft in
 * the middle, the selected section's AI instructions and sources on the right.
 */
const FirstDraft = ({ output, onDirtyChange: reportDirty }: FirstDraftProps) => {
  const queryClient = useQueryClient();
  const { data: project } = useProject(output.project_id);
  const sections = useSections(output.id);
  const mutations = useOutlineMutations(output.id);
  const renameOutput = useUpdateOutput(output.id);
  const evidence = useProjectEvidence(output.project_id);
  const access = useCanWrite(output.project_id, project?.my_role);
  const canWrite = access.canWrite && output.stage === "first_draft";

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const { isBlocked, confirmLeave, cancelLeave, guardedNavigate: guard } = useUnsavedChangesGuard(dirty);
  const onDirtyChange = (next: boolean) => {
    setDirty(next);
    reportDirty?.(next);
  };
  const list = sections.data ?? [];
  const selected = list.find((s) => s.id === selectedId) ?? list[0] ?? null;

  useEffect(() => {
    // The selected section was deleted (or none chosen yet): fall back to the first.
    if (selected && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected, selectedId]);

  const stopEditing = () => {
    setEditingId(null);
    onDirtyChange(false);
  };

  const onError = (title: string, error: unknown) => {
    if (isConflict(error)) {
      toast({ title: "Someone else changed this", description: "We've loaded the latest version. Please re-apply your change.", variant: "destructive" });
      void mutations.refetch();
      void queryClient.invalidateQueries({ queryKey: outputKeys.output(output.id) });
      return;
    }
    toast({ title, description: (error as Error).message, variant: "destructive" });
  };

  if (sections.error) return <ErrorBanner message={`Could not load the outline: ${sections.error.message}`} />;
  if (sections.isLoading) return <div className="py-12 text-center text-muted-foreground">Loading the draft...</div>;

  return (
    <div className="space-y-3">
      <UnsavedChangesDialog open={isBlocked} onConfirm={confirmLeave} onCancel={cancelLeave} />
      {!canWrite && access.reason && output.stage === "first_draft" && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">{access.reason}</p>
      )}
      <div className="grid gap-5 lg:grid-cols-[16rem_minmax(0,1fr)_20rem] lg:items-start">
        <OutlinePanel
          output={output}
          sections={list}
          selectedId={selected?.id ?? null}
          onSelect={setSelectedId}
          canWrite={canWrite}
          mutations={mutations}
          onError={onError}
        />
        <DraftCanvas
          output={output}
          sections={list}
          selectedId={selected?.id ?? null}
          editingId={editingId}
          canWrite={canWrite}
          savingTitle={renameOutput.isPending}
          savingSection={mutations.update.isPending}
          onSelect={setSelectedId}
          onEdit={(id) =>
            guard(() => {
              onDirtyChange(false);
              setEditingId(id);
              setSelectedId(id);
            })
          }
          onCancel={() => guard(stopEditing)}
          onDirtyChange={onDirtyChange}
          onSave={(section, json, text) =>
            mutations.update.mutate(
              { id: section.id, patch: { version: section.version, content_json: json, content_text: text } },
              { onSuccess: stopEditing, onError: (e) => onError("Could not save the section", e) },
            )
          }
          onRenameOutput={(title) =>
            renameOutput.mutate({ version: output.version, title }, { onError: (e) => onError("Could not rename the output", e) })
          }
        />
        {selected ? (
          <SectionPanel
            key={selected.id}
            section={selected}
            documents={evidence.documents}
            documentsById={evidence.byId}
            canWrite={canWrite}
            mutations={mutations}
            onError={onError}
          />
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

export default FirstDraft;
